// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ATTENTION_SEVERITY_LABELS,
  type ActiveAttentionSeverity,
  type AttentionReason,
} from '@/constants/attention';
import {
  ErrorStatusIcon,
  InfoStatusIcon,
  WarningStatusIcon,
} from '@/constants/hardfin-standard-icons';
import type { Shipment } from '@/shipment/api/types';
import { getShipmentCycleTimeAttentionTooltip } from '@/shipment/constants/attention';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

interface ShipmentTimeInStatusProps {
  shipment: Pick<Shipment, 'attention' | 'timeInStatusDays'>;
}

const ATTENTION_SEVERITY_COLOR: Record<ActiveAttentionSeverity, string> = {
  notice: 'info.main',
  warning: 'warning.main',
  critical: 'error.main',
};

function AttentionIcon(props: { severity: ActiveAttentionSeverity }) {
  const iconProps = {
    fontSize: 'small' as const,
    sx: { flexShrink: 0 },
  };

  if (props.severity === 'critical') {
    return <ErrorStatusIcon {...iconProps} />;
  }

  if (props.severity === 'warning') {
    return <WarningStatusIcon {...iconProps} />;
  }

  return <InfoStatusIcon {...iconProps} />;
}

function AttentionTooltipContent(props: {
  severity: ActiveAttentionSeverity;
  reason: AttentionReason;
  daysInState: number;
}) {
  return (
    <Typography
      component="div"
      variant="caption"
      sx={{
        color: 'inherit',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.4,
        py: 0.25,
      }}
    >
      <Box component="span" sx={{ fontWeight: 600 }}>
        {ATTENTION_SEVERITY_LABELS[props.severity]}:{' '}
      </Box>
      {getShipmentCycleTimeAttentionTooltip(props.reason, props.daysInState)}
    </Typography>
  );
}

function TimeInStatusText(props: {
  children: ReactNode;
  color: string;
  fontWeight?: number;
}) {
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{
        color: props.color,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: props.fontWeight,
      }}
    >
      {props.children}
    </Typography>
  );
}

export function ShipmentTimeInStatus(props: ShipmentTimeInStatusProps) {
  const { timeInStatusDays } = props.shipment;

  if (timeInStatusDays === null) {
    return (
      <TimeInStatusText color="text.secondary" fontWeight={400}>
        —
      </TimeInStatusText>
    );
  }

  const wholeDays = Math.floor(timeInStatusDays);
  const cycleTimeAttention = props.shipment.attention?.cycleTime;

  if (!cycleTimeAttention) {
    return (
      <TimeInStatusText color="text.secondary" fontWeight={400}>
        {wholeDays}d
      </TimeInStatusText>
    );
  }

  return (
    <Tooltip
      arrow
      title={
        <AttentionTooltipContent
          severity={cycleTimeAttention.severity}
          reason={cycleTimeAttention.reason}
          daysInState={wholeDays}
        />
      }
    >
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Stack
          component="span"
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={0.5}
          sx={{
            color: ATTENTION_SEVERITY_COLOR[cycleTimeAttention.severity],
          }}
        >
          <AttentionIcon severity={cycleTimeAttention.severity} />
          <TimeInStatusText color="inherit" fontWeight={600}>
            {wholeDays}d
          </TimeInStatusText>
        </Stack>
      </Box>
    </Tooltip>
  );
}
